import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { Search, Images, Loader2 } from 'lucide-react';
import { PHOTO_GALLERY_TOUR_ID } from '@/features/tours/photo-gallery';
import { useTour } from '@/features/tours';
import { BaseLayout } from '@/shared/components/layout';
import { Input } from '@/shared/ui/input';
import { Progress } from '@/shared/ui/progress';
import { Skeleton } from '@/shared/ui/skeleton';
import { Card, CardContent } from '@/shared/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Label } from '@/shared/ui/label';
import { useInfinitePhotoGroups, useUploadPhotos } from '../hooks';
import type {
  PhotoGalleryFilters,
  PhotoGroup,
  PhotoGroupsResponse,
  PhotoSourceType,
} from '../types';
import { PhotoGalleryGrid } from './PhotoGalleryGrid';
import { PhotoUploadZone } from './PhotoUploadZone';

const PHOTO_PAGE_SIZE = 24;

const sourceOptions: { label: string; value: 'all' | PhotoSourceType }[] = [
  { label: 'All Sources', value: 'all' },
  { label: 'Gallery', value: 'gallery' },
  { label: 'Observations', value: 'observation' },
  { label: 'Punch Lists', value: 'punchlist' },
  { label: 'Drone Photos', value: 'drone_photo' },
  { label: 'Other', value: 'other' },
];

interface UploadingPhotoPreview {
  id: string;
  name: string;
  progress: number;
}

const mergePhotoPages = (pages: PhotoGroupsResponse[] | undefined): PhotoGroup[] => {
  const groupsByDate = new Map<string, PhotoGroup>();

  pages?.forEach((page) => {
    page.groups.forEach((group) => {
      const existing = groupsByDate.get(group.date);
      if (existing) {
        existing.photos.push(...group.photos);
        return;
      }
      groupsByDate.set(group.date, { ...group, photos: [...group.photos] });
    });
  });

  return Array.from(groupsByDate.values());
};

export const PhotoGalleryPage: React.FC = () => {
  const { projectIdNumber: numericProjectId } = useCurrentProject();

  const [filters, setFilters] = useState<PhotoGalleryFilters>({});
  const [uploadingPreviews, setUploadingPreviews] = useState<UploadingPhotoPreview[]>([]);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  } = useInfinitePhotoGroups(numericProjectId, filters, PHOTO_PAGE_SIZE);
  const uploadMutation = useUploadPhotos(numericProjectId, 'gallery');

  const photoGroups = useMemo(() => mergePhotoPages(data?.pages), [data?.pages]);

  const totalPhotos = useMemo(
    () => data?.pages[0]?.count ?? 0,
    [data?.pages],
  );
  const loadedPhotos = useMemo(
    () => photoGroups.reduce((sum, group) => sum + group.photos.length, 0),
    [photoGroups],
  );

  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (!numericProjectId) return;
    if (hasSeen(PHOTO_GALLERY_TOUR_ID)) return;
    void startTour(PHOTO_GALLERY_TOUR_ID);
  }, [tourHydrated, active, startTour, hasSeen, numericProjectId]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || isFetchingNextPage) return;
        void fetchNextPage();
      },
      { rootMargin: '600px 0px' },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, loadedPhotos]);

  return (
    <BaseLayout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <Images className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold">Photo Gallery</h1>
          <span className="text-sm text-muted-foreground">({totalPhotos})</span>
        </div>

        <div data-tour-id="photo-gallery-upload">
          <PhotoUploadZone
            disabled={!numericProjectId}
            isUploading={uploadMutation.isPending}
            onUpload={async (files) => {
              const previews = files.map((file, index) => ({
                id: `${file.name}-${index}-${Date.now()}`,
                name: file.name,
                progress: 0,
              }));
              setUploadingPreviews(previews);

              try {
                await uploadMutation.mutateAsync({
                  files,
                  onFileProgress: (index, progressPct) => {
                    setUploadingPreviews((prev) =>
                      prev.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, progress: Math.min(100, Math.max(0, Math.round(progressPct))) }
                          : item,
                      ),
                    );
                  },
                });
              } finally {
                setTimeout(() => setUploadingPreviews([]), 500);
              }
            }}
          />
        </div>

        {uploadingPreviews.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              Uploading {uploadingPreviews.length} photo{uploadingPreviews.length === 1 ? '' : 's'}...
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {uploadingPreviews.map((upload) => (
                <Card key={upload.id} className="overflow-hidden">
                  <CardContent className="p-3 space-y-2">
                    <Skeleton className="w-full aspect-square rounded-md animate-pulse" />
                    <p className="text-xs truncate" title={upload.name}>
                      {upload.name}
                    </p>
                    <Progress value={upload.progress} className="h-2" />
                    <p className="text-[11px] text-muted-foreground">{upload.progress}%</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-tour-id="photo-gallery-filters">
          <div className="md:col-span-2">
            <Label htmlFor="photo-search" className="text-xs text-muted-foreground">
              Search
            </Label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="photo-search"
                className="pl-8"
                placeholder="Search by filename or uploader"
                value={filters.search ?? ''}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, search: event.target.value || undefined }))
                }
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Source</Label>
            <Select
              value={filters.source_type ?? 'all'}
              onValueChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  source_type: value === 'all' ? undefined : (value as PhotoSourceType),
                }))
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                {sourceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="date-from" className="text-xs text-muted-foreground">
              Date From
            </Label>
            <Input
              id="date-from"
              type="date"
              className="mt-1"
              value={filters.date_from ?? ''}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, date_from: event.target.value || undefined }))
              }
            />
          </div>
          <div>
            <Label htmlFor="date-to" className="text-xs text-muted-foreground">
              Date To
            </Label>
            <Input
              id="date-to"
              type="date"
              className="mt-1"
              value={filters.date_to ?? ''}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, date_to: event.target.value || undefined }))
              }
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading photos...</div>
        ) : isError ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              Unable to load photos. Please try again.
            </CardContent>
          </Card>
        ) : (
          <div data-tour-id="photo-gallery-grid">
            <PhotoGalleryGrid groups={photoGroups} />
            {photoGroups.length > 0 && (
              <div ref={loadMoreRef} className="flex min-h-16 items-center justify-center py-6">
                {isFetchingNextPage ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading more photos...
                  </div>
                ) : hasNextPage ? (
                  <span className="text-sm text-muted-foreground">
                    Showing {loadedPhotos} of {totalPhotos} photos
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    Showing all {totalPhotos} photo{totalPhotos === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </BaseLayout>
  );
};
